import * as Sentry from '@sentry/react';
import { useMemo } from 'react';
import { useStrategyStore, useThroughputTabStore } from '../../../../../../store/strategy-store/strategy-store';
import { collectChronologicalDates } from '../main-section.helpers';
import type { ChartDataItem } from './contributing-outcomes-chart.type';

/**
 * Hook that prepares data for the Contributing Outcomes chart
 *
 * Transforms the raw outcome data into a format suitable for the bar chart,
 * where each data point contains:
 * - date: The time period label
 * - [outcomeTitle]: The value for each selected outcome
 *
 * @returns {ChartDataItem[] | []} An array of data points for the chart, where each item
 * contains a date and values for each selected outcome
 */
const useChartData = (): ChartDataItem[] | [] => {
  const initiative = useStrategyStore((state) => state.initiativePerformance) as any;
  const selectedOutcomes = useThroughputTabStore((state) => state.selectedOutcomes);
  const metric = useThroughputTabStore((state) => state.metric);

  const chartData = useMemo(() => {
    try {
      if (!initiative) {
        return [];
      }

      const dates = collectChronologicalDates(initiative.contributing_outcomes_over_time, metric);

      const data = dates.map((date) => {
        const dataEntries = selectedOutcomes.map((outcome) => {
          const total = Object.values(outcome.contributing_teams_over_time ?? {}).reduce((sum, value) => {
            const metricValues = value?.[metric];
            if (!metricValues) {
              return sum;
            }

            return sum + (metricValues[date] ?? 0);
          }, 0);

          return [outcome.id, total];
        });

        return {
          date,
          ...Object.fromEntries(dataEntries),
        };
      });

      return data;
    } catch (error) {
      Sentry.captureException(error);
      return [];
    }
  }, [initiative, selectedOutcomes, metric]);

  return chartData;
};

export { useChartData };
